import {CaseHal} from '../models/HAL/case-hal.model';
import {DocumentHal} from '../models/HAL/document-hal.model';
import {sanitizeDownloadFilename} from './filename-sanitizer';

export const CASE_ZIP_FILENAME_TEMPLATE_FALLBACK = '{CASE_NAME} - {CLIENT_NAME} - {DATE} - {SHORT_TIME}';

/**
 * Builds the ZIP filename used when downloading all (or a selection of) documents of a case.
 *
 * The filename is rendered from `caseHal.downloadCaseZipFilenameTemplate` (falling back to
 * {@link CASE_ZIP_FILENAME_TEMPLATE_FALLBACK} when the template is missing OR when any of the
 * placeholders it references resolves to an empty value) using these tokens: `CASE_NAME`,
 * `CLIENT_NAME`, `ASSIGNEE_NAME`, `CREATOR_NAME`, `CASE_REFERENCE`, `CASE_ID`, `EXTERNAL_CASE_ID`,
 * `CREATION_DATE`, `CREATION_TIME`, `CREATION_DATETIME`, `DATE`, `TIME`, `SHORT_TIME`,
 * `DATETIME`, `LAST_UPLOAD_DATE`, `LAST_UPLOAD_TIME`, `SELECTED_FILES_COUNT`.
 *
 * `LAST_UPLOAD_*` is derived from the latest `lastUpdated` of `includedDocuments`.
 * `SELECTED_FILES_COUNT` reflects `filesCount` (selected docs in case-detail flow,
 * all downloadable docs in whole-case overview flow).
 *
 * The result is sanitized via {@link sanitizeDownloadFilename}; if it collapses to empty,
 * a `FormsHubCockpit_Download_<timestamp>` base is used. The `.zip` extension is always present.
 */
export function buildCaseZipFilename(caseHal: CaseHal | undefined,
                                     includedDocuments: DocumentHal[],
                                     filesCount: number,
                                     now: Date = new Date()): string {
  if (!caseHal) {
    return ensureZipExtension(`FormsHubCockpit_Download_${formatDateForFilename(now, 'yyyy-MM-dd_HH-mm-ss')}`);
  }
  const placeholders = buildPlaceholders(caseHal, includedDocuments, filesCount, now);
  const template = chooseTemplate(caseHal, placeholders);
  const resolved = template.replaceAll(TEMPLATE_TOKEN_REGEX, (match, token) => placeholders[token] ?? match);
  const sanitized = sanitizeDownloadFilename(resolved);
  const fallbackBase = `FormsHubCockpit_Download_${formatDateForFilename(now, 'yyyy-MM-dd_HH-mm-ss')}`;
  const baseName = sanitized.trim() ? sanitized : fallbackBase;
  return ensureZipExtension(baseName);
}

const TEMPLATE_TOKEN_REGEX = /\{([A-Z0-9_]+)}/g;

/**
 * Returns the configured template if all placeholders it references resolve to non-empty values;
 * otherwise returns the fallback template.
 */
function chooseTemplate(caseHal: CaseHal, placeholders: Record<string, string>): string {
  const configured = caseHal.downloadCaseZipFilenameTemplate;
  if (!configured?.trim()) {
    return CASE_ZIP_FILENAME_TEMPLATE_FALLBACK;
  }
  const referencedTokens = [...configured.matchAll(TEMPLATE_TOKEN_REGEX)].map(match => match[1]);
  const hasEmptyPlaceholder = referencedTokens.some(token => token in placeholders && placeholders[token] === '');
  return hasEmptyPlaceholder ? CASE_ZIP_FILENAME_TEMPLATE_FALLBACK : configured;
}

function buildPlaceholders(caseHal: CaseHal,
                           includedDocuments: DocumentHal[],
                           filesCount: number,
                           now: Date): Record<string, string> {
  const creationDate = formatDateForFilename(caseHal.creationDate, 'yyyy-MM-dd');
  const creationTime = formatDateForFilename(caseHal.creationDate, 'HH-mm-ss');
  const creationDateTime = formatDateForFilename(caseHal.creationDate, 'yyyy-MM-dd_HH-mm-ss');
  const currentDate = formatDateForFilename(now, 'yyyy-MM-dd');
  const currentTime = formatDateForFilename(now, 'HH-mm-ss');
  const currentShortTime = formatDateForFilename(now, 'HH-mm');
  const currentDateTime = formatDateForFilename(now, 'yyyy-MM-dd_HH-mm-ss');
  const latestUploadDate = getMostRecentUploadDate(includedDocuments);
  const lastUploadDate = formatDateForFilename(latestUploadDate, 'yyyy-MM-dd');
  const lastUploadTime = formatDateForFilename(latestUploadDate, 'HH-mm-ss');

  return {
    CASE_NAME: caseHal.name ?? '',
    CLIENT_NAME: caseHal.client ?? '',
    ASSIGNEE_NAME: caseHal.assignee ?? '',
    CREATOR_NAME: caseHal.creator ?? '',
    CASE_REFERENCE: caseHal.reference ?? '',
    CASE_ID: caseHal.identification ?? '',
    EXTERNAL_CASE_ID: caseHal.externalCaseId ?? '',
    CREATION_DATE: creationDate,
    CREATION_TIME: creationTime,
    CREATION_DATETIME: creationDateTime,
    DATE: currentDate,
    TIME: currentTime,
    SHORT_TIME: currentShortTime,
    DATETIME: currentDateTime,
    LAST_UPLOAD_DATE: lastUploadDate,
    LAST_UPLOAD_TIME: lastUploadTime,
    SELECTED_FILES_COUNT: filesCount.toString()
  };
}

export function formatDateForFilename(date: Date | undefined, format: string): string {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return format
    .replaceAll('yyyy', String(year))
    .replaceAll('MM', month)
    .replaceAll('dd', day)
    .replaceAll('HH', hours)
    .replaceAll('mm', minutes)
    .replaceAll('ss', seconds);
}

function getMostRecentUploadDate(documents: DocumentHal[]): Date | undefined {
  return documents
    .map(document => document.lastUpdated)
    .filter((date): date is Date => date instanceof Date && !Number.isNaN(date.getTime()))
    .reduce<Date | undefined>((currentLatest, date) => {
      if (!currentLatest || date > currentLatest) {
        return date;
      }
      return currentLatest;
    }, undefined);
}

function ensureZipExtension(value: string): string {
  return value.toLowerCase().endsWith('.zip') ? value : `${value}.zip`;
}


